import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "../AuthContext";
import { StatusBadge } from "./Dashboard";
import { toast } from "sonner";

export function AdminUsers() {
  const { API } = useAuth();
  const [users, setUsers] = useState([]);

  const load = () => axios.get(`${API}/admin/users`, { withCredentials: true }).then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);

  const decide = async (uid, decision) => {
    await axios.post(`${API}/admin/users/${uid}/teacher-decision`, { decision }, { withCredentials: true });
    toast.success(`Teacher ${decision}`);
    load();
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="overline">Management</div>
        <h1 className="font-heading text-3xl md:text-4xl font-semibold text-[#0A231C]">Users</h1>
      </div>
      <div className="card-surface overflow-x-auto">
        <table className="lms-table w-full">
          <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Teacher Status</th><th className="text-right pr-4">Action</th></tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.user_id} data-testid={`user-row-${u.user_id}`}>
                <td>
                  <div className="flex items-center gap-3">
                    <img src={u.picture || `https://i.pravatar.cc/40?u=${u.email}`} alt="" className="w-8 h-8 rounded-full" />
                    <span className="font-heading font-medium">{u.name}</span>
                  </div>
                </td>
                <td className="text-[#5C5C5C]">{u.email}</td>
                <td className="capitalize">{u.role}</td>
                <td>{u.teacher_status ? <StatusBadge s={u.teacher_status} /> : <span className="text-xs text-[#A3A3A3]">—</span>}</td>
                <td className="text-right pr-4">
                  {u.role === "teacher" && u.teacher_status === "pending" && (
                    <>
                      <button onClick={() => decide(u.user_id, "approved")} className="text-sm mr-3 text-[#2E7D32]" data-testid={`approve-teacher-${u.user_id}`}>Approve</button>
                      <button onClick={() => decide(u.user_id, "rejected")} className="text-sm text-[#C2482A]" data-testid={`reject-teacher-${u.user_id}`}>Reject</button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function AdminCourses() {
  const { API } = useAuth();
  const [courses, setCourses] = useState([]);
  const [filter, setFilter] = useState("pending");

  const load = () => axios.get(`${API}/courses?status=${filter}`, { withCredentials: true }).then((r) => setCourses(r.data));
  useEffect(() => { load(); }, [filter]);

  const decide = async (id, decision, price) => {
    await axios.post(`${API}/admin/courses/${id}/decision`, { decision, price }, { withCredentials: true });
    toast.success(`Course ${decision}`);
    load();
  };

  const updatePrice = async (id, price) => {
    await axios.patch(`${API}/courses/${id}`, { price: parseFloat(price) }, { withCredentials: true });
    toast.success("Price updated");
    load();
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="overline">Review</div>
          <h1 className="font-heading text-3xl md:text-4xl font-semibold text-[#0A231C]">Courses</h1>
        </div>
        <div className="flex gap-2">
          {["draft", "pending", "approved", "rejected"].map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              data-testid={`filter-${s}`}
              className={`text-sm px-4 py-2 rounded-full border ${filter === s ? "bg-[#0A231C] text-white border-[#0A231C]" : "border-[#E5E3D8]"}`}
            >{s}</button>
          ))}
        </div>
      </div>

      <div className="card-surface overflow-x-auto">
        <table className="lms-table w-full">
          <thead><tr><th>Course</th><th>Instructor</th><th>Category</th><th>Price</th><th>Status</th><th className="text-right pr-4">Action</th></tr></thead>
          <tbody>
            {courses.map((c) => (
              <tr key={c.id} data-testid={`admin-course-row-${c.id}`}>
                <td className="font-heading font-medium">{c.title}</td>
                <td>{c.instructor_name}</td>
                <td>{c.category}</td>
                <td>
                  <input
                    type="number"
                    defaultValue={c.price}
                    onBlur={(e) => e.target.value !== String(c.price) && updatePrice(c.id, e.target.value)}
                    className="w-20 px-2 py-1 border border-[#E5E3D8] rounded"
                    data-testid={`price-input-${c.id}`}
                  />
                </td>
                <td><StatusBadge s={c.status} /></td>
                <td className="text-right pr-4">
                  {c.status === "pending" && (
                    <>
                      <button onClick={() => decide(c.id, "approved")} className="text-sm mr-3 text-[#2E7D32]" data-testid={`approve-course-${c.id}`}>Approve</button>
                      <button onClick={() => decide(c.id, "rejected")} className="text-sm text-[#C2482A]" data-testid={`reject-course-${c.id}`}>Reject</button>
                    </>
                  )}
                  <a href={`/courses/${c.id}`} className="text-sm text-[#0A231C] hover:underline ml-3">View</a>
                </td>
              </tr>
            ))}
            {courses.length === 0 && <tr><td colSpan={6} className="text-center text-sm text-[#5C5C5C] py-8">Nothing here.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function AdminVideos() {
  const { API } = useAuth();
  const [courses, setCourses] = useState([]);

  useEffect(() => {
    axios.get(`${API}/courses`, { withCredentials: true }).then((r) => setCourses(r.data));
  }, []);

  return (
    <div className="space-y-6">
      <div>
        <div className="overline">Source files</div>
        <h1 className="font-heading text-3xl md:text-4xl font-semibold text-[#0A231C]">Videos</h1>
        <p className="text-sm text-[#5C5C5C] mt-2">Only IT Admin can download source videos.</p>
      </div>
      <div className="space-y-4">
        {courses.map((c) => <VideoCourseRow key={c.id} course={c} />)}
      </div>
    </div>
  );
}

function VideoCourseRow({ course }) {
  const { API } = useAuth();
  const [details, setDetails] = useState(null);
  const [open, setOpen] = useState(false);

  const toggle = async () => {
    if (!details) {
      const r = await axios.get(`${API}/courses/${course.id}`, { withCredentials: true });
      setDetails(r.data);
    }
    setOpen(!open);
  };

  return (
    <div className="card-surface">
      <button onClick={toggle} className="w-full flex items-center justify-between p-4 text-left">
        <div>
          <div className="font-heading font-medium">{course.title}</div>
          <div className="text-xs text-[#5C5C5C]">{course.instructor_name}</div>
        </div>
        <StatusBadge s={course.status} />
      </button>
      {open && details && (
        <ul className="border-t border-[#F0EDE3] divide-y divide-[#F0EDE3]">
          {details.videos.length === 0 ? (
            <li className="p-4 text-sm text-[#5C5C5C]">No videos.</li>
          ) : details.videos.map((v) => (
            <li key={v.id} className="p-4 flex items-center justify-between">
              <div>
                <div className="font-heading font-medium text-sm">{v.title}</div>
                <div className="text-xs text-[#5C5C5C]">{v.original_filename}</div>
              </div>
              <a href={`${API}/videos/${v.id}/download`} className="btn-outline text-sm" data-testid={`admin-download-video-${v.id}`}>Download</a>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function AdminWebinars() {
  const { API } = useAuth();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("pending");

  const load = () => axios.get(`${API}/webinars`, { params: { status: filter }, withCredentials: true }).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [filter]);

  const decide = async (id, decision, price) => {
    await axios.post(`${API}/admin/webinars/${id}/decision`, { decision, price }, { withCredentials: true });
    toast.success(`Webinar ${decision}`);
    load();
  };

  const updatePrice = async (id, price) => {
    await axios.patch(`${API}/webinars/${id}`, { price: parseFloat(price) }, { withCredentials: true });
    toast.success("Price updated");
    load();
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="overline">Review</div>
          <h1 className="font-heading text-3xl md:text-4xl font-semibold text-[#0A231C]">Webinars</h1>
        </div>
        <div className="flex gap-2">
          {["draft", "pending", "approved", "rejected"].map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              data-testid={`wfilter-${s}`}
              className={`text-sm px-4 py-2 rounded-full border ${filter === s ? "bg-[#0A231C] text-white border-[#0A231C]" : "border-[#E5E3D8]"}`}
            >{s}</button>
          ))}
        </div>
      </div>

      <div className="card-surface overflow-x-auto">
        <table className="lms-table w-full">
          <thead><tr><th>Title</th><th>Instructor</th><th>Category</th><th>When</th><th>Price (₹)</th><th>Status</th><th className="text-right pr-4">Action</th></tr></thead>
          <tbody>
            {items.map((w) => (
              <tr key={w.id} data-testid={`admin-webinar-row-${w.id}`}>
                <td className="font-heading font-medium">{w.title}</td>
                <td>{w.instructor_name}</td>
                <td>{w.category}</td>
                <td className="text-xs text-[#5C5C5C]">{new Date(w.scheduled_at).toLocaleString()}</td>
                <td>
                  <input
                    type="number"
                    defaultValue={w.price}
                    onBlur={(e) => e.target.value !== String(w.price) && updatePrice(w.id, e.target.value)}
                    className="w-24 px-2 py-1 border border-[#E5E3D8] rounded"
                    data-testid={`webinar-price-input-${w.id}`}
                  />
                </td>
                <td><StatusBadge s={w.status || "draft"} /></td>
                <td className="text-right pr-4">
                  {w.status === "pending" && (
                    <>
                      <button onClick={() => decide(w.id, "approved")} className="text-sm mr-3 text-[#2E7D32]" data-testid={`approve-webinar-${w.id}`}>Approve</button>
                      <button onClick={() => decide(w.id, "rejected")} className="text-sm text-[#C2482A]" data-testid={`reject-webinar-${w.id}`}>Reject</button>
                    </>
                  )}
                  <a href={`/webinars/${w.id}`} className="text-sm text-[#0A231C] hover:underline ml-3">View</a>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={7} className="text-center text-sm text-[#5C5C5C] py-8">Nothing here.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

